import { Head } from '@inertiajs/react';
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import Toast from '@/Components/Toast';
import AppNav from './Partials/AppNav';
import PublicAppNav from './Partials/PublicAppNav';
import { ThemeProvider, useTheme } from '@/Contexts/ThemeProvider';
import UiSettingsProvider from '@/Contexts/UiSettingsProvider';
import useTypedPage from '@/Hooks/useTypedPage';
import { ModeProvider } from '@/Contexts/ModeProvider';
import { IntercomProvider } from 'react-use-intercom';
import OnboardingVideo from './Partials/OnboardingVideo';
import { PostHogProvider } from 'posthog-js/react/dist/umd/index.js';
import TopBanner from '@/Components/TopBanner';
import { cn } from '@/utils';
import useTopBanner from '@/Hooks/useTopBanner';
import { MarkerProvider } from '@/Contexts/MarkerProvider';
import MessageProvider from '@/Contexts/MessageProvider';
import useRoute from '@/Hooks/useRoute';

interface Props {
  title: string;
  mobileSupported?: boolean;
  jsonLD?: string;
  renderHeader?(): JSX.Element;
  renderSidebar?(): JSX.Element;
}

function Layout({
  title,
  mobileSupported = false,
  jsonLD,
  renderSidebar,
  renderHeader,
  children,
}: PropsWithChildren<Props>) {
  const { original_user_id, meta_tags, auth, onboarding_video, toast, file } =
    useTypedPage().props;
  const { theme } = useTheme();
  const route = useRoute();
  const { topBanner, showTopBanner, dismissTopBanner } = useTopBanner();
  const isSnippetViewVisible = route().current('snippet.show', {
    file: file?.id,
  });

  useEffect(() => {
    document.querySelector('html')?.classList.add(theme);
    return () => {
      document.querySelector('html')?.classList.remove(theme);
    };
  }, [theme]);

  return (
    <div className={`${theme} app-container text-base`}>
      <Head title={meta_tags?.title || title}>
        {Object.entries(meta_tags)
          .filter(item => item[1])
          .map(item => (
            <meta key={item[0]} name={item[0]} content={item[1]} />
          ))}
        {jsonLD && (
          <script type="application/ld+json">{JSON.stringify(jsonLD)}</script>
        )}
      </Head>
      {!mobileSupported && (
        <div className="fixed z-[100] flex h-screen w-screen items-center justify-center bg-brand-dark-100/60 p-8 text-center text-brand-light-100 backdrop-blur-md md:hidden">
          <p className="mx-auto max-w-lg text-xl">
            Please view on a desktop resolution. We do not currently support
            mobile devices.
          </p>
        </div>
      )}
      {showTopBanner && (
        <TopBanner
          topBanner={topBanner}
          showTopBanner={showTopBanner}
          dismissTopBanner={dismissTopBanner}
        />
      )}
      <div
        className={cn(
          'flex h-screen w-screen divide-x divide-brand-dark-15/20 overflow-hidden bg-brand-dark-100/10 text-brand-dark-100/40 dark:divide-brand-dark-100 dark:bg-brand-dark-75 dark:text-brand-light-25',
          {
            'border-4 border-red-500': original_user_id,
          },
          {
            'h-[calc(100vh-52px)]': showTopBanner,
          }
        )}
      >
        {auth.user ? <AppNav /> : <PublicAppNav />}
        <div
          className={cn(
            'flex h-full flex-1 flex-col overflow-hidden bg-brand-light-100 dark:bg-brand-dark-100',
            {
              'pl-16 lg:pl-0': isSnippetViewVisible,
            }
          )}
        >
          {renderHeader ? (
            <header className="z-10">{renderHeader()}</header>
          ) : null}
          <main className="flex-1 overflow-y-auto border-t border-t-brand-25 bg-brand-light-100 dark:border-t-brand-dark-20 dark:bg-brand-dark-100">
            {children}
          </main>
        </div>
        {renderSidebar ? (
          <aside className="h-full flex-shrink-0 bg-brand-light-75 dark:bg-brand-dark-75">
            {renderSidebar()}
          </aside>
        ) : null}
      </div>
      {onboarding_video?.src && (
        <OnboardingVideo
          videoSrc={onboarding_video.src}
          videoTitle={onboarding_video.title}
        />
      )}
      {toast && <Toast toast={toast} />}
    </div>
  );
}

export default function AppLayout(props: PropsWithChildren<Props>) {
  return (
    <PostHogProvider
      apiKey={
        import.meta.env.VITE_APP_ENV !== 'local' &&
        typeof window !== 'undefined'
          ? import.meta.env.VITE_POSTHOG_API_KEY
          : null
      }
      options={{
        api_host: import.meta.env.VITE_POSTHOG_APP_URL
          ? 'https://' + import.meta.env.VITE_POSTHOG_APP_URL
          : 'https://app.posthog.com',
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            password: true,
          },
        },
      }}
    >
      <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
        <MarkerProvider project={import.meta.env.VITE_MARKER_WIDGET_ID}>
          <ModeProvider>
            <MessageProvider>
              <ThemeProvider>
                <UiSettingsProvider>
                  <Layout {...props} />
                </UiSettingsProvider>
              </ThemeProvider>
            </MessageProvider>
          </ModeProvider>
        </MarkerProvider>
      </IntercomProvider>
    </PostHogProvider>
  );
}
